import * as RadixDialog from '@radix-ui/react-dialog'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Icon } from '../../Icon'
import { TextField } from '../../common/fields/TextField'
import Divider from './Divider'
import Heading from './Heading'

type ModalProps = {
    size?: 'small' | 'medium' | 'large'
    children: React.ReactNode
    title?: React.ReactNode
    isOpen: boolean
    onClose: VoidFunction
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void
    onNavigateBack?: VoidFunction
    onSearch?: (searchText: string) => void
    footer?: React.ReactNode
    showDividers?: boolean
    width?: number
    onInteractOutside?: React.ComponentProps<typeof RadixDialog.Content>['onInteractOutside']
}

function Modal({
    children,
    size = 'large',
    title,
    footer,
    isOpen,
    onClose,
    onNavigateBack,
    onScroll,
    onSearch,
    showDividers = true,
    width,
    onInteractOutside,
}: ModalProps) {
    const [shouldDisableCloseOnClickOutside, setShouldDisableCloseOnClickOutside] = useState(false)
    const contextValue = useMemo(
        () => ({ setShouldDisableCloseOnClickOutside }),
        [setShouldDisableCloseOnClickOutside]
    )

    const stdWidth = size === 'large' ? 744 : size === 'medium' ? 600 : 480
    return (
        <ModalContext.Provider value={contextValue}>
            <RadixDialog.Root
                open={isOpen}
                onOpenChange={() => {
                    if (shouldDisableCloseOnClickOutside) {
                        return
                    }
                    onClose()
                }}
            >
                <RadixDialog.Portal>
                    <RadixDialog.Overlay className="fixed inset-0 top-0 right-0 bottom-0 left-0 bg-gray-700/40">
                        <RadixDialog.Content
                            className="mx-auto flex! h-screen flex-col justify-center"
                            style={{ width: width ?? stdWidth }}
                            onInteractOutside={(e) => onInteractOutside?.(e)}
                        >
                            <RadixDialog.Description className="hidden">
                                Modal
                            </RadixDialog.Description>

                            <div className="flex max-h-[calc(100vh-64px)] flex-col items-stretch justify-start rounded-lg bg-white">
                                {/* header */}
                                <div
                                    data-testid="modal-header"
                                    className={twm(
                                        'flex items-center justify-between',
                                        size === 'small' ? 'mx-6 mt-6 mb-0' : 'mx-8 my-6'
                                    )}
                                >
                                    {Boolean(onNavigateBack) && (
                                        <button
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                onNavigateBack?.()
                                            }}
                                            className="group relative flex size-8 shrink-0 items-center justify-center"
                                        >
                                            <div className="absolute -inset-3 z-10 rounded-lg group-hover:bg-black/4" />
                                            <Icon icon="chevronLeft.medium" />
                                        </button>
                                    )}
                                    <RadixDialog.Title asChild>
                                        <Heading size={size === 'small' ? 'sm' : 'xs'}>
                                            {title}
                                        </Heading>
                                    </RadixDialog.Title>
                                    <div className="h-full pt-1">
                                        <RadixDialog.Close asChild>
                                            <button
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation()
                                                    onClose()
                                                }}
                                                className="group relative flex size-8 shrink-0 items-center justify-center"
                                            >
                                                <div className="absolute -inset-3 z-10 rounded-lg group-hover:bg-black/4" />
                                                <Icon icon="close.medium" className="z-20" />
                                            </button>
                                        </RadixDialog.Close>
                                    </div>
                                </div>

                                {showDividers && <Divider className="my-0" />}

                                {/* body */}
                                {Boolean(onSearch) && (
                                    <div
                                        className={twm(
                                            'mt-5 mb-2',
                                            size === 'small' ? 'mx-6' : 'mx-8'
                                        )}
                                    >
                                        <TextField
                                            autoFocus
                                            size="medium"
                                            isFullWidth
                                            placeholder="Search"
                                            icon="magnifier.medium"
                                            onChange={(e) => onSearch?.(e.target.value)}
                                        />
                                    </div>
                                )}
                                <div className="relative overflow-y-auto" onScroll={onScroll}>
                                    <div
                                        className={twm(
                                            size === 'small' ? 'mx-6 my-6' : 'mx-8 my-8'
                                        )}
                                    >
                                        {children}
                                    </div>
                                </div>

                                {/* footer */}
                                {footer && (
                                    <>
                                        {showDividers && <Divider className="my-0" />}
                                        <footer
                                            className={twm(
                                                'my-6 flex justify-end',
                                                size === 'small' ? 'mx-6' : 'mx-8'
                                            )}
                                        >
                                            {footer}
                                        </footer>
                                    </>
                                )}
                            </div>
                        </RadixDialog.Content>
                    </RadixDialog.Overlay>
                </RadixDialog.Portal>
            </RadixDialog.Root>
        </ModalContext.Provider>
    )
}
export default Modal

// This context exists specifically for the DatePicker component, but may be useful
// for others in the future. The DatePicker component renders itself in something
// akin to a portal. But this means that when you click in the date picker, the
// modal picks this up as a click outside and closes before the date picker can
// receive the event.
//
// This context is used to disable the close on click outside when the date picker
// is open.
type ModalContextType = {
    setShouldDisableCloseOnClickOutside: (shouldDisable: boolean) => void
}

const ModalContext = createContext<ModalContextType | null>(null)

export const useModalContext = () => {
    return useContext(ModalContext)
}
